import { gql, useMutation } from '@apollo/client'
import { Button } from '@mui/material'
import {
  Mutation,
  CreatePlatformAccessRequestParams,
} from '@promoboxx/graphql-gateway-types'
import { Field, Form, Formik } from 'formik'
import { TextField } from 'formik-mui'
import Honeybadger from 'honeybadger-js'
import I18n from 'i18n-js'
import { useState } from 'react'
import { useHistory } from 'react-router-dom'
import * as Yup from 'yup'

import ConfirmDialog from '@src/lib/ConfirmDialog'
import MenuItemWithCheckbox from '@src/lib/MenuItemWithCheckbox'

import AuthCardHeader from '../AuthCardHeader'

type Vertical = {
  key: string
  value: string
}

const options: Vertical[] = [
  { key: 'animal_nutrition', value: 'Animal Nutrition' },
  { key: 'appliances', value: 'Appliances' },
  { key: 'automotive', value: 'Automotive' },
  { key: 'cycling', value: 'Cycling' },
  { key: 'flooring', value: 'Flooring' },
  { key: 'footwear', value: 'Footwear Athletic' },
  { key: 'health_beauty', value: 'Health & Beauty' },
  { key: 'luxury', value: 'Luxury Jewelry & Watches' },
  { key: 'outdoor', value: 'Outdoor General Sporting Goods' },
  { key: 'petcare', value: 'Pet Care' },
  { key: 'none_of_the_above', value: 'None of the Above' },
]

const schema = Yup.object()
  .shape({
    name: Yup.string().required().label(I18n.t('auth.yourName')),
    email_address: Yup.string()
      .email()
      .required()
      .label(I18n.t('shared.emailAddress')),
    info: Yup.string()
      .max(255)
      .required()
      .label(I18n.t('requestAccessForm.infoField')),
    vertical: Yup.string()
      .required()
      .label(I18n.t('requestAccessForm.verticalField')),
    industry: Yup.string()
      .when('vertical', {
        is: 'none_of_the_above',
        then: Yup.string().required().min(1).max(128),
      })
      .label(I18n.t('requestAccessForm.industryField')),
  })
  .label('Field')

const CREATE_PLATFORM_ACCESS_REQUEST = gql`
  mutation createPlatformAccessRequest(
    $params: CreatePlatformAccessRequestParams!
  ) {
    createPlatformAccessRequest(platform_access_request: $params) {
      id
    }
  }
`
const translateVerticalName = (vertical: string) => {
  return I18n.t(`requestAccessForm.verticalOptions.${vertical}`, {
    defaultValue: vertical,
  })
}

const RequestAccessForm: React.FC<React.PropsWithChildren> = () => {
  const [createRequest] = useMutation<Mutation>(CREATE_PLATFORM_ACCESS_REQUEST)
  const [showDialog, setShowDialog] = useState(false)
  const history = useHistory()
  const onSubmit = (data: CreatePlatformAccessRequestParams) => {
    createRequest({
      variables: {
        params: {
          ...data,
          vertical: options.find((el: Vertical) => el.key === data.vertical)
            ?.value,
        },
      },
    })
      .then(() => {
        setShowDialog(true)
      })
      .catch(() => {
        Honeybadger.notify(
          new Error(
            `Failed to create platform access request for ${data.email_address}`,
          ),
        )
      })
  }

  return (
    <>
      <AuthCardHeader header={I18n.t('auth.requestAccessToPromoboxx')} />
      <Formik
        initialValues={{
          name: '',
          email_address: '',
          info: '',
          vertical: '',
          industry: '',
        }}
        onSubmit={onSubmit}
        validationSchema={schema}
      >
        {({ values, isSubmitting, isValid }) => (
          <Form>
            <Field
              component={TextField}
              label={I18n.t('auth.yourName')}
              name="name"
              type="text"
              fullWidth
            />
            <Field
              component={TextField}
              label={I18n.t('shared.emailAddress')}
              name="email_address"
              fullWidth
              type="email"
            />
            <Field
              component={TextField}
              label={I18n.t('shared.howCanWeHelpYouQM')}
              name="info"
              fullWidth
              multiline
              minRows="2"
            />
            <Field
              component={TextField}
              name="vertical"
              label={I18n.t('requestAccessForm.vertical')}
              size="small"
              fullWidth
              select
            >
              {options.map((el: Vertical) => {
                return (
                  <MenuItemWithCheckbox value={el.key} key={el.key}>
                    {translateVerticalName(el.key)}
                  </MenuItemWithCheckbox>
                )
              })}
            </Field>
            {values.vertical === 'none_of_the_above' && (
              <Field
                component={TextField}
                label={I18n.t('requestAccessForm.industry')}
                name="industry"
                fullWidth
                multiline
              />
            )}
            <Button
              color="primary"
              disabled={isSubmitting || !isValid}
              type="submit"
              variant="contained"
            >
              {I18n.t('requestMoreCampaigns.submit')}
            </Button>
          </Form>
        )}
      </Formik>
      <ConfirmDialog
        onClose={() => {
          setShowDialog(false)
          history.push('/login')
        }}
        open={showDialog}
        title={I18n.t('requestAccessForm.title')}
        content={I18n.t('requestAccessForm.formSuccess')}
      />
    </>
  )
}

export default RequestAccessForm
